import { useState, useEffect } from "react";
import { Tables } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { parseFlickrEmbed } from "@/utils/flickr";

interface LocationEditDialogProps {
  location: Tables<"locations"> | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (locationId: string, updates: Partial<Tables<"locations">>) => Promise<void>;
}

export const LocationEditDialog = ({
  location,
  isOpen,
  onClose,
  onSave,
}: LocationEditDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    summary: "",
    description: "",
    recommendation: "",
    image_path: "",
    category: "",
    latitude: "",
    longitude: "",
    status: "new" as "new" | "approved" | "rejected" | "pending_verification",
    photo_session_hidden: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Fetch categories for the dropdown
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || "",
        summary: (location as any).summary || "",
        description: location.description || "",
        recommendation: location.recommendation || "",
        image_path: location.image_path || "",
        category: location.category || "none",
        latitude: location.latitude?.toString() || "",
        longitude: location.longitude?.toString() || "",
        status: location.status,
        photo_session_hidden: (location as any).photo_session_hidden ?? false,
      });
    }
  }, [location]);

  const handleSave = async () => {
    if (!location) return;

    setIsSaving(true);
    try {
      const updates: Partial<Tables<"locations">> = {
        name: formData.name,
        summary: formData.summary.trim() ? formData.summary.trim() : null,
        description: formData.description || null,
        recommendation: formData.recommendation || null,
        image_path: formData.image_path || null,
        category: formData.category === "none" ? null : formData.category || null,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        status: formData.status,
        photo_session_hidden: formData.photo_session_hidden,
      } as any;

      await onSave(location.id, updates);
      onClose();
    } catch (error) {
      console.error("Error saving location:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!location) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Locatie bewerken</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Locatie naam"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="new">Nieuw</SelectItem>
                  <SelectItem value="approved">Goedgekeurd</SelectItem>
                  <SelectItem value="pending_verification">In afwachting</SelectItem>
                  <SelectItem value="rejected">Afgekeurd</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <small className="block text-xs text-muted-foreground -mt-1">
              Eén zin onder de titel. Wordt gebruikt op de detail‑pagina als korte introductie. Alleen jij kan dit veld invullen.
            </small>
            <Input
              id="summary"
              type="text"
              maxLength={200}
              value={formData.summary}
              onChange={(e) => handleInputChange("summary", e.target.value)}
              placeholder="Bv. 'Een rustig stukje natuur vlakbij de spoorlijn, voor wandelingen of een boekje in het gras.'"
            />
            <div className="text-[12.5px]" style={{ color: "var(--muted)" }}>
              {formData.summary.length}/200
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Beschrijving van de locatie"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recommendation">Aanbeveling</Label>
            <Textarea
              id="recommendation"
              value={formData.recommendation}
              onChange={(e) => handleInputChange("recommendation", e.target.value)}
              placeholder="Waarom moet je deze plek zeker bezoeken?"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="image_path">Flickr embedcode</Label>
              <small className="block text-xs text-muted-foreground -mt-1">
                Plak hier de volledige Flickr-embedcode (via "Deel" → "Embed" op Flickr). De foto linkt dan automatisch terug naar Flickr, zoals de Flickr-voorwaarden vereisen.
              </small>
              <Textarea
                id="image_path"
                value={formData.image_path}
                onChange={(e) => handleInputChange("image_path", e.target.value)}
                placeholder='<a data-flickr-embed="true" href="https://www.flickr.com/photos/..." title="..."><img src="https://live.staticflickr.com/..." width="2048" height="1365" alt="..."/></a><script async src="//embedr.flickr.com/assets/client-code.js" charset="utf-8"></script>'
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Categorie</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => handleInputChange("category", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer een categorie..." />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="none">Geen categorie</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="latitude">Breedtegraad *</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => handleInputChange("latitude", e.target.value)}
                placeholder="51.0261"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="longitude">Lengtegraad *</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => handleInputChange("longitude", e.target.value)}
                placeholder="4.4775"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="photo_session_hidden">Fotosessie-blok verbergen</Label>
              <p className="text-sm text-muted-foreground">
                Verberg het "Photo Session Booking"-blok op deze locatie.
              </p>
            </div>
            <Switch
              id="photo_session_hidden"
              checked={formData.photo_session_hidden}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, photo_session_hidden: checked }))
              }
            />
          </div>

          {formData.image_path && parseFlickrEmbed(formData.image_path) && (
            <div className="space-y-2">
              <Label>Voorvertoning afbeelding</Label>
              <img
                src={parseFlickrEmbed(formData.image_path)!.imgSrc}
                alt="Preview"
                className="w-full max-w-md h-32 object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || !formData.name || !formData.latitude || !formData.longitude}
          >
            {isSaving ? "Opslaan..." : "Opslaan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};